import { canDecideOwnAccessRequest, canReviewAccessRequests } from '@infras/shared-types';
import type { Me } from '../../lib/useMe';
import { statusBadgeClass } from '../../lib/access';
import type { MentionUser } from '../../lib/access';
import type { TaskAccessView } from '../../lib/requestTypes';
import { Markdown } from '../Markdown';
import { Icon } from '../Icon';
import { entityHref, typeLabel } from './AttributeSidebar';
import { formatDate, formatDateTime } from '../../lib/format';
import { Link } from 'react-router';
import { useLayoutEffect, useRef } from 'react';

/**
 * What the viewer may do on an access task. The API re-checks everything; these drive
 * buttons, not enforcement (mirrors inventory-core `canDecideRow` / `assertDecider`):
 *   decide (approve | deny) → root | maintainer OR any assignee of the task (any role,
 *   new members included); never the requester, except root deciding their own (marked
 *   self-decided) · cancel → the server's `canCancel` (requester | root) · edit the
 *   decision note → root | maintainer, once decided.
 */
export function useAccessPermissions(
  r: TaskAccessView | undefined,
  me: Me | null | undefined,
  note: string,
  serverCanCancel: boolean,
) {
  const myEmail = (me?.email ?? '').toLowerCase();
  const isRequester = !!r && r.requesterEmail.toLowerCase() === myEmail;
  const isAssignee = !!r?.assignees.includes(myEmail);
  const reviewerRole = !!me && canReviewAccessRequests(me.role);
  // The decide gate, before the own-task rule.
  const mayDecide = !!me && (reviewerRole || isAssignee);
  // Root may decide its own task (self-documented grant); everyone else keeps four-eyes.
  const selfDecide = isRequester && !!me && canDecideOwnAccessRequest(me.role);
  const pending = r?.status === 'pending';
  const canDecide = pending && mayDecide && (!isRequester || selfDecide);
  // Requester who would otherwise be allowed (an assignee or maintainer filing their own).
  const ownBlocked = pending && mayDecide && isRequester && !selfDecide;
  const canCancel = pending && serverCanCancel;
  const decideTitle = !note.trim()
    ? 'Enter a decision note first'
    : selfDecide
      ? 'This is your own task — the decision will be marked self-decided'
      : '';

  return {
    myEmail,
    isRequester,
    isAssignee,
    canDecide,
    ownBlocked,
    selfDecide,
    pending,
    canCancel,
    decideTitle,
    hasActions: canDecide || canCancel,
  };
}

export type AccessPermissions = ReturnType<typeof useAccessPermissions>;

interface AccessBoxProps {
  r: TaskAccessView;
  p: AccessPermissions;
  busy: boolean;
  mentionUsers: MentionUser[];
  note: string;
  setNote: (v: string) => void;
  onDecide: (approve: boolean) => void;
  onAskCancel: () => void;
}

/** "a, b or c" — the people who may decide, by local part. */
function whoList(emails: string[]): string {
  const names = emails.map((e) => e.split('@')[0]);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/**
 * The access part of an access task, kept compact: system + target and the grant status.
 * Pending → the viewer's actions (decision note + Approve / Deny, Cancel for the
 * requester or root). Decided → one result line and the note. Everything else is said
 * in comments.
 */
export function AccessBox(props: AccessBoxProps) {
  const { r, p, busy, note, setNote } = props;
  const noteRef = useRef<HTMLTextAreaElement>(null);
  // Autosize: one 34px line when empty, grows with the note (no inner scroll until 240px).
  useLayoutEffect(() => {
    const el = noteRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight + 2, 34), 240)}px`;
  }, [note]);
  const href = entityHref(r);
  const validity = r.dueAt ? `Access until ${formatDate(r.dueAt)}` : 'Permanent';
  const verb = r.status === 'approved' ? 'Approved' : r.status === 'denied' ? 'Denied' : r.status === 'cancelled' ? 'Cancelled' : r.status;

  return (
    <section className="rq-access" aria-label="Access" data-testid="req-decision">
      <div className="rq-access__head">
        <Icon name="access" className="req-ico" />
        <span className="rq-access__what">
          <span className="muted">{typeLabel(r.resourceType)}</span>
          {' · '}
          {href ? (
            <Link className="req-link" to={href}>
              {r.resourceName}
            </Link>
          ) : (
            <strong className="rq-break">{r.resourceName}</strong>
          )}
        </span>
        <span className={statusBadgeClass(r.status)} data-testid="req-rail-status">
          {r.status}
        </span>
      </div>

      {!p.pending && (
        <div className="rq-access__body" data-testid="req-access-result">
          <p className="rq-access__line">
            <strong>{verb}</strong>
            {r.decidedBy && <> by {r.decidedBy}</>}
            {r.decidedAt && <span className="muted"> · {formatDateTime(r.decidedAt)}</span>}
            {r.status === 'approved' && (
              <span className="muted" data-testid="req-rail-validity">
                {' '}
                · {validity}
              </span>
            )}
          </p>
          {r.decisionNote && (
            <div className="rq-access__note tl-body">
              <Markdown>{r.decisionNote}</Markdown>
            </div>
          )}
        </div>
      )}

      {p.pending && (
        <div className="rq-access__body">
          {p.canDecide ? (
            <div data-testid="req-decide">
              <textarea
                ref={noteRef}
                className="rq-access__note-input"
                rows={1}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (required)"
                aria-label="Decision note"
                disabled={busy}
                data-testid="req-decision-note"
              />
              {p.selfDecide && (
                <p className="rq-access__hint">Your own task — the decision is marked self-decided.</p>
              )}
            </div>
          ) : (
            <p className="rq-access__line muted" data-testid={p.ownBlocked ? 'req-decision-own' : undefined}>
              {p.ownBlocked
                ? 'Your own task — another assignee or a root/maintainer decides it.'
                : r.assignees.length
                  ? `Waiting on ${whoList(r.assignees)} or a root/maintainer.`
                  : 'Waiting on a root or maintainer.'}
            </p>
          )}
          {p.hasActions && (
            <div className="req-actions">
              {p.canDecide && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy || !note.trim()}
                    title={p.decideTitle}
                    data-testid="req-approve"
                    onClick={() => props.onDecide(true)}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    disabled={busy || !note.trim()}
                    title={p.decideTitle}
                    data-testid="req-deny"
                    onClick={() => props.onDecide(false)}
                  >
                    Deny
                  </button>
                </>
              )}
              {p.canCancel && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={props.onAskCancel}>
                  Cancel task
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {r.answers && r.answers.length > 0 && (
        <dl className="rq-access__answers" data-testid="req-answers">
          {r.answers.map((a) => (
            <div key={a.sortOrder}>
              <dt className="muted text-xs">{a.prompt}</dt>
              <dd>
                <Markdown>{a.answer}</Markdown>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
